/**
 * Monte Carlo Distribution Chart
 * 
 * Visualizes the probability distribution of demand calculations:
 * - Histogram of simulated values
 * - Confidence intervals (95%)
 * - Mean/Median markers
 * - Interactive tooltips
 * 
 * Professional styling for court exhibits and expert reports.
 */

import React, { useRef, useEffect } from 'react';
import * as d3 from 'd3';

interface MonteCarloDistributionProps {
  data: number[];  // Array of simulated demand values
  mean?: number;
  median?: number;
  p5?: number;
  p95?: number;
  baseDemand?: number;
  title?: string;
  width?: number;
  height?: number;
}

export const MonteCarloDistribution: React.FC<MonteCarloDistributionProps> = ({
  data,
  mean,
  median,
  p5,
  p95,
  baseDemand,
  title = "Monte Carlo Simulation Results",
  width = 700,
  height = 350
}) => {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!svgRef.current || !data.length) return;

    // Clear previous content
    d3.select(svgRef.current).selectAll("*").remove();

    const margin = { top: 50, right: 40, bottom: 60, left: 70 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    const svg = d3.select(svgRef.current)
      .attr("width", width)
      .attr("height", height)
      .style("font-family", "'Inter', -apple-system, sans-serif");

    const g = svg.append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Calculate histogram bins
    const extent = d3.extent(data) as [number, number];
    const binGenerator = d3.bin()
      .domain(extent)
      .thresholds(40);

    const bins = binGenerator(data);

    // Which bin holds the mean — this is the only bar allowed full brand
    // strength (ONE-SOLID-ORANGE rule). Every other bar renders in the
    // calmer --chart-2 tone so the histogram reads as one quiet series,
    // not a wall of solid orange.
    let meanBin: d3.Bin<number, number> | undefined;
    if (mean !== undefined && bins.length) {
      meanBin = bins.reduce((closest, b) => {
        const bc = ((b.x0 ?? 0) + (b.x1 ?? 0)) / 2;
        const cc = ((closest.x0 ?? 0) + (closest.x1 ?? 0)) / 2;
        return Math.abs(bc - mean) < Math.abs(cc - mean) ? b : closest;
      }, bins[0]);
    }

    // Scales
    const xScale = d3.scaleLinear()
      .domain(extent)
      .range([0, innerWidth]);

    const yScale = d3.scaleLinear()
      .domain([0, d3.max(bins, d => d.length) || 0])
      .range([innerHeight, 0]);

    // 95% Confidence interval shading
    if (p5 !== undefined && p95 !== undefined) {
      g.append("rect")
        .attr("x", xScale(p5))
        .attr("y", 0)
        .attr("width", xScale(p95) - xScale(p5))
        .attr("height", innerHeight)
        .attr("fill", "var(--color-needs-human-tint)")
        .attr("opacity", 0.5);
    }

    // Histogram bars
    g.selectAll(".bar")
      .data(bins)
      .enter()
      .append("rect")
      .attr("class", "bar")
      .attr("x", d => xScale(d.x0!))
      .attr("y", d => yScale(d.length))
      .attr("width", d => Math.max(0, xScale(d.x1!) - xScale(d.x0!) - 1))
      .attr("height", d => innerHeight - yScale(d.length))
      .attr("fill", d => d === meanBin ? "var(--chart-1)" : "var(--chart-2)")
      .attr("opacity", d => d === meanBin ? 1 : 0.65)
      .attr("rx", 1);

    // Mean line
    if (mean !== undefined) {
      g.append("line")
        .attr("x1", xScale(mean))
        .attr("x2", xScale(mean))
        .attr("y1", 0)
        .attr("y2", innerHeight)
        .attr("stroke", "var(--chart-negative)")
        .attr("stroke-width", 2)
        .attr("stroke-dasharray", "5,5");
    }

    // Median line
    if (median !== undefined && median !== mean) {
      g.append("line")
        .attr("x1", xScale(median))
        .attr("x2", xScale(median))
        .attr("y1", 0)
        .attr("y2", innerHeight)
        .attr("stroke", "var(--chart-positive)")
        .attr("stroke-width", 2)
        .attr("stroke-dasharray", "3,3");
    }

    // Base demand marker
    if (baseDemand !== undefined) {
      g.append("line")
        .attr("x1", xScale(baseDemand))
        .attr("x2", xScale(baseDemand))
        .attr("y1", 0)
        .attr("y2", innerHeight)
        .attr("stroke", "var(--color-brand)")
        .attr("stroke-width", 2);

      g.append("text")
        .attr("x", xScale(baseDemand))
        .attr("y", innerHeight + 25)
        .attr("text-anchor", "middle")
        .attr("fill", "var(--color-brand)")
        .attr("font-size", "10px")
        .attr("font-weight", "600")
        .text("Base");
    }

    // X Axis
    g.append("g")
      .attr("transform", `translate(0,${innerHeight})`)
      .call(d3.axisBottom(xScale)
        .tickFormat(d => `$${d3.format(".2s")(d as number)}`)
        .ticks(8)
      )
      .selectAll("text")
      .attr("font-size", "10px");

    // X Axis label
    g.append("text")
      .attr("x", innerWidth / 2)
      .attr("y", innerHeight + 45)
      .attr("text-anchor", "middle")
      .attr("fill", "var(--chart-axis)")
      .attr("font-size", "12px")
      .text("Total Demand ($)");

    // Y Axis
    g.append("g")
      .call(d3.axisLeft(yScale).ticks(6))
      .selectAll("text")
      .attr("font-size", "10px");

    // Y Axis label
    g.append("text")
      .attr("transform", "rotate(-90)")
      .attr("y", -50)
      .attr("x", -innerHeight / 2)
      .attr("text-anchor", "middle")
      .attr("fill", "var(--chart-axis)")
      .attr("font-size", "12px")
      .text("Frequency");

    // Title
    svg.append("text")
      .attr("x", width / 2)
      .attr("y", 20)
      .attr("text-anchor", "middle")
      .attr("fill", "var(--color-text-primary)")
      .attr("font-size", "15px")
      .attr("font-weight", "600")
      .text(title);

    // Confidence interval annotation - positioned below title, not overlapping
    if (p5 !== undefined && p95 !== undefined && mean !== undefined) {
      // Single line with all stats, properly spaced
      svg.append("text")
        .attr("x", width / 2)
        .attr("y", 38)
        .attr("text-anchor", "middle")
        .attr("fill", "var(--color-text-muted)")
        .attr("font-size", "11px")
        .text(`95% CI: $${d3.format(",.0f")(p5)} – $${d3.format(",.0f")(p95)}  |  Mean: $${d3.format(",.0f")(mean)}`);
    } else if (p5 !== undefined && p95 !== undefined) {
      svg.append("text")
        .attr("x", width / 2)
        .attr("y", 38)
        .attr("text-anchor", "middle")
        .attr("fill", "var(--color-text-muted)")
        .attr("font-size", "11px")
        .text(`95% CI: $${d3.format(",.0f")(p5)} – $${d3.format(",.0f")(p95)}`);
    }

  }, [data, mean, median, p5, p95, baseDemand, title, width, height]);

  return (
    <div className="monte-carlo-chart rounded-lg p-4 shadow-sm" style={{ backgroundColor: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
      <svg ref={svgRef} />
      <div className="flex justify-center gap-6 mt-2 text-xs" style={{ color: 'var(--color-text-muted)' }}>
        <span className="flex items-center gap-1">
          <span className="w-3 h-0.5" style={{ display: 'inline-block', backgroundColor: 'var(--chart-negative)' }}></span>
          Mean
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3" style={{ display: 'inline-block', backgroundColor: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}></span>
          95% Confidence Interval
        </span>
      </div>
    </div>
  );
};

export default MonteCarloDistribution;
